<template>
  <div
    class="carousel__external"
    :class="carouselExternalWidthClass"
    tabindex="-1"
  >
    <button
      class="carousel__arrow carousel__leftArrow"
      :aria-disabled="currentIndex === 0"
      :style="{ backgroundColor: backgroundColorArrow }"
      @click="prevSlide"
      @keydown="handlePrevKeyDown"
    >
      <svg
        class="carousel__arrowImage"
        width="15"
        height="24"
        viewBox="0 0 15 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          :fill="colorArrow"
          d="M12.0003 0L14.8203 2.82L5.66031 12L14.8203 21.18L12.0003 24L0.000312805 12L12.0003 0Z"
        />
      </svg>
    </button>

    <button
      class="carousel__arrow carousel__rightArrow"
      :aria-disabled="currentIndex === innerDivCount - 1"
      :style="{ backgroundColor: backgroundColorArrow }"
      @click="nextSlide"
      @keydown="handleNextKeyDown"
    >
      <svg
        class="carousel__arrowImage"
        width="15"
        height="24"
        viewBox="0 0 15 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          :fill="colorArrow"
          d="M2.99969 0L0.179688 2.82L9.33969 12L0.179688 21.18L2.99969 24L14.9997 12L2.99969 0Z"
        />
      </svg>
    </button>

    <div
      class="carousel"
      :class="carouselWidthClass"
    >
      <div
        v-if="width === 'full'"
        class="carousel__indicators"
        @click="pushGTM"
      >
        <button
          v-for="(title, index) in titlesArray"
          :key="index"
          class="carousel__indicator"
          :class="{ carousel__active: currentIndex === index }"
          :aria-label="title"
          :aria-current="currentIndex === index"
          @keypress="goToSlide(index)"
          @click="goToSlide(index)"
        />
      </div>

      <div
        ref="carouselContainer"
        class="carousel__container"
        :style="containerStyle"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'c-carousel',
  props: {
    width: {
      type: String,
      default: 'mid',
      validator(value) {
        return ['full', 'mid'].includes(value)
      },
    },
    backgroundColorArrow: {
      type: String,
      default: '#074E8E',
    },
    colorArrow: {
      type: String,
      default: '#fff',
    },
  },
  data() {
    return {
      currentIndex: 0,
      innerDivCount: 0,
      containerTranslate: '',
      titlesArray: [],
    }
  },
  computed: {
    carouselWidthClass() {
      return this.width === 'mid' ? 'carousel--mid' : 'carousel--full'
    },
    carouselExternalWidthClass() {
      return this.width === 'mid'
        ? 'carousel__external--mid'
        : 'carousel__external--full'
    },

    containerWidth() {
      return `${this.innerDivCount * 100}%`
    },
    containerStyle() {
      return {
        width: this.containerWidth,
        transform: `translateX(${this.containerTranslate})`,
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.countChildren()
      this.collectTitles()
      this.updateContainerTranslate()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    prevSlide() {
      if (this.currentIndex > 0) {
        this.currentIndex--
      } else {
        this.currentIndex = this.innerDivCount - 1
      }
      this.updateContainerTranslate()
    },
    nextSlide() {
      if (this.currentIndex < this.innerDivCount - 1) {
        this.currentIndex++
      } else {
        this.currentIndex = 0
      }
      this.updateContainerTranslate()
    },
    goToSlide(index) {
      this.currentIndex = index
      this.updateContainerTranslate()
    },
    getContainer() {
      return this.$refs.carouselContainer
    },
    countChildren() {
      const container = this.getContainer()
      if (container) {
        this.innerDivCount = Array.from(container.children).filter(
          (child) => child.nodeType === 1
        ).length
      }
    },
    updateContainerTranslate() {
      const container = this.getContainer()
      if (!container) return

      const children = Array.from(container.children).filter(child => child.nodeType === 1)

      children.forEach((child, index) => {
        if (index === this.currentIndex) {
          child.setAttribute('aria-hidden', 'false')
          child.removeAttribute('tabindex')
        } else {
          child.setAttribute('aria-hidden', 'true')
          child.setAttribute('tabindex', '-1')
        }
      })

      const slideWidth = children[0]?.getBoundingClientRect().width || 0
      const offset = this.currentIndex * slideWidth
      this.containerTranslate = `-${offset}px`
    },
    collectTitles() {
      const titles = []
      const container = this.getContainer()
      if (container) {
        const titleDivs = container.querySelectorAll('.c-title')
        titleDivs.forEach((div) => {
          titles.push(div.textContent.trim())
        })
      }
      this.titlesArray = titles
    },
    handleResize() {
      this.updateContainerTranslate()
    },
    handleNextKeyDown(event) {
      if (event.key === 'Tab' && !event.shiftKey && this.currentIndex < this.innerDivCount - 1) {
        event.preventDefault()
        this.nextSlide()
      }
    },
    handlePrevKeyDown(event) {
      if (event.key === 'Tab' && event.shiftKey && this.currentIndex > 0) {
        event.preventDefault()
        this.prevSlide()
      }
    },
    pushGTM(event) {
      const target = event.target.closest('button')
      const index = this.currentIndex

      window.dataLayer.push({
        event: 'click',
        section: 'banner',
        outbound: false,
        position: index,
        name: this.titlesArray[index] || '',
        label: target?.innerText || target?.getAttribute('aria-label') || ''
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.carousel {
  display: block;
  overflow: hidden;
  position: relative;

  &__external {
    position: relative;

    &--mid {
      margin: 0 auto;
      width: 84.88%;

      @media screen and (min-width: 992px) {
        width: 71.42%;
      }

      .carousel__arrow {
        top: 15%;

        @media screen and (min-width: 576px) {
          top: 20%;
        }

        @media screen and (min-width: 768px) {
          top: 30%;
        }
      }

      .carousel__leftArrow {
        transform: translate(-50%, -50%);
        left: 0;

        @media screen and (min-width: 992px) {
          transform: translate(-100%, -50%);
          left: -2.5rem;
        }
      }

      .carousel__rightArrow {
        transform: translate(50%, -50%);
        right: 0;

        @media screen and (min-width: 992px) {
          transform: translate(100%, -50%);
          right: -2.5rem;
        }
      }
    }

    &--full {
      .carousel__arrow {
        top: 50%;
      }

      .carousel__leftArrow {
        display: flex;
        left: 0.2rem;

        @media screen and (min-width: 425px) {
          left: 0.5rem;
          margin-left: 0.5rem;
        }

        @media screen and (min-width: 768px) {
          left: 1rem;
        }

        @media screen and (min-width: 992px) {
          left: 2rem;
          margin-left: unset;
        }
      }

      .carousel__rightArrow {
        display: flex;
        right: 0.2rem;

        @media screen and (min-width: 425px) {
          right: 0.5rem;
          margin-right: 0.5rem;
        }

        @media screen and (min-width: 768px) {
          right: 1rem;
        }

        @media screen and (min-width: 992px) {
          right: 2rem;
          margin-right: unset;
        }
      }
    }
  }

  &--mid {
    display: block;
    margin: 0 auto;
    position: relative;
  }

  &__indicators {
    display: flex;
    left: 50%;
    gap: 0.5rem;
    position: absolute;
    transform: translate(-50%, -50%);
    top: 95%;
    z-index: 1;

    @media screen and (min-width: 992px) {
      gap: 1rem;
      top: 90%;
    }

    @media screen and (min-width: 1200px) {
      gap: 1.5rem;
      top: 80%;
    }
  }

  &__indicator {
    background: $white;
    cursor: pointer;
    height: 1rem;
    width: 1rem;
    border-radius: 50%;
  }

  &__active {
    background: #bdd9ea;
  }

  &__arrow {
    align-items: center;
    cursor: pointer;
    display: flex;
    justify-content: center;
    height: 2rem;
    position: absolute;
    width: 2rem;
    z-index: 1;

    @media screen and (min-width: 768px) {
      transform: translate(0%, -50%);
      height: 4.5rem;
      width: 4.5rem;
    }

    .carousel__arrowImage {
      width: 0.463rem;
      height: 0.75rem;

      @media screen and (min-width: 768px) {
        height: 1.5rem;
        width: 0.926rem;
      }
    }
  }

  &__container {
    display: flex;
    transition: transform 0.5s ease-in-out;
  }
}
</style>
